import { useTranslation } from 'react-i18next';
import Grid from '@mui/material/Grid';
import type { SubmitHandler } from 'react-hook-form';
import { FormProvider, useForm } from 'react-hook-form';
import Stack from '@mui/material/Stack';
import { zodResolver } from '@hookform/resolvers/zod';
import Box from '@mui/material/Box';
import { useEffect, useState } from 'react';
import omit from 'lodash/omit';
import { Button, Loader } from '@repo/ui';
import type { MerchantResponse } from '@repo/api/endpoints/merchant';
import { colorPalette } from '@repo/mui-config/color-palette';
import { useIsMobile } from '@repo/mui-config/hooks';
import { LimitMerchant, OfferLimit, providerType, ProviderType } from '@repo/shared/types';
import { MIN_LOAN_AMOUNT } from '@repo/shared/constants';
import { providerLimitValues } from '@repo/store/helpers';
import { useEventClick } from '@repo/hooks';

import type { LoanStoreData } from '@/components/loan-process/store/loan-process.store';
import {
  LOAN_PROCESS_STATE,
  useLoanProcessStore,
} from '@/components/loan-process/store/loan-process.store';
import { LoanProcessModalHeader } from '@/components/loan-process/loan-process-modal/loan-process-modal-header';
import { useMainHubStoreActions } from '@/store/main-hub.store';
import { VIEW_TYPE } from '@/store/view.type';
import type { HandleSubmitParams } from '@/components/loan-process/loan-stages/stage-2/stage-two';

import { ShopList } from './shop-list';
import { useAgreementAndApprovalsContext } from './context/use-agreement-and-approvals-context';
import type { AgreementAndApprovalsFields } from './agreement-and-approvals.schema';
import { AgreementAndApprovalsSchema } from './agreement-and-approvals.schema';
import { AgreementAndApprovalsSummary } from './summary/agreement-and-approvals-summary';
import { Documents } from './documents';

const getFormDefaultValues = ({
  merchantResponseData,
  loanData,
  loanLimit,
  hasBeenEditRequestSent,
  selectedProvider,
}: {
  merchantResponseData: MerchantResponse;
  loanData: LoanStoreData;
  loanLimit: LimitMerchant | OfferLimit;
  hasBeenEditRequestSent: boolean;
  selectedProvider: ProviderType;
}): AgreementAndApprovalsFields | undefined => {
  return {
    requestId: merchantResponseData.requestId,
    loanId: loanData.id,
    provider: selectedProvider,
    editedAmount: hasBeenEditRequestSent ? loanData.amount : loanLimit.available,
    originalAmount: loanData.amount,
  };
};
interface AgreementAndApprovalsContent {
  merchantResponseData: MerchantResponse;
  loanData: LoanStoreData;
  loanLimit: LimitMerchant | OfferLimit;
  hasBeenEditRequestSent: boolean;
  isApplicationProcessing: boolean;
  selectedProvider: ProviderType;
  handleSubmitForm: (data: HandleSubmitParams) => void;
  handleAcceptTerms: () => void;
}

export function AgreementAndApprovalsContent({
  loanData,
  merchantResponseData,
  handleSubmitForm,
  isApplicationProcessing,
  loanLimit,
  selectedProvider,
  handleAcceptTerms,
  hasBeenEditRequestSent,
}: AgreementAndApprovalsContent) {
  const { t } = useTranslation();
  const [isInitiallySetEditMode, setIsInitiallySetEditMode] = useState<boolean>(false);

  const isMobile = useIsMobile();
  const {
    actions: { clearLoanProcessStore },
    state: { viewOptions },
  } = useLoanProcessStore();
  const { setCurrentView } = useMainHubStoreActions();

  const editModeOnInit = viewOptions[LOAN_PROCESS_STATE.AGREEMENT_AND_APPROVALS].openInEditMode;

  const providerLimit = providerLimitValues(merchantResponseData, selectedProvider);

  const calculateLoanLimitAvailable = () => {
    const amount = loanData.amount;
    if (!providerLimit) {
      return amount;
    }
    const { max, available } = providerLimit;
    if (amount === max) {
      return max;
    }
    if (amount < max) {
      if (available >= 0) {
        return amount + available;
      }

      return amount;
    }

    return amount;
  };

  const loanLimitAvailable = {
    ...loanLimit,
    available: calculateLoanLimitAvailable(),
  };

  const defaultValues = getFormDefaultValues({
    merchantResponseData,
    loanData,
    loanLimit: loanLimitAvailable,
    hasBeenEditRequestSent,
    selectedProvider,
  });

  const { isEditMode, setIsEditMode } = useAgreementAndApprovalsContext();
  const methods = useForm<AgreementAndApprovalsFields>({
    defaultValues,
    resolver: zodResolver(AgreementAndApprovalsSchema),
  });

  const onSubmit: SubmitHandler<AgreementAndApprovalsFields> = (data) => {
    methods.setValue('originalAmount', data.editedAmount);
    setIsEditMode(false);
    const formData = {
      ...omit(data, 'editedAmount', 'originalAmount'),
      data: {
        period: loanData.period,
        amount: methods.getValues().originalAmount,
      },
    };
    handleSubmitForm({
      formData,
      amount: {
        originalAmount: methods.getValues().originalAmount,
        initialOriginalAmount: defaultValues?.originalAmount,
      },
    });
  };

  useEffect(() => {
    if (editModeOnInit && !isInitiallySetEditMode) {
      setIsEditMode(editModeOnInit);
      setIsInitiallySetEditMode(false);
    }
  }, [editModeOnInit, isInitiallySetEditMode, setIsEditMode]);

  const handleClickCloseButton = () => {
    clearLoanProcessStore();
    setCurrentView(VIEW_TYPE.DASHBOARD);
  };

  const isSelectedLibierisLoan = selectedProvider === providerType.enum.liberis;
  const trackClick = useEventClick(merchantResponseData);

  return (
    <Box
      data-testid="agreement-and-approvals"
      sx={{
        px: isMobile ? 2 : 3,
        py: 2,
        backgroundColor: isMobile ? colorPalette.cloud.light : colorPalette.white,
        position: 'relative',
      }}
    >
      {isApplicationProcessing ? (
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            right: 0,
            bottom: 0,
            left: 0,
            backdropFilter: 'blur(2px)',
            zIndex: 20,
          }}
        >
          <Loader
            sx={{
              position: 'sticky',
              left: '50%',
              top: '50%',
            }}
          />
        </Box>
      ) : null}
      <LoanProcessModalHeader
        closeButton={{ onClick: handleClickCloseButton }}
        containerSx={{ mb: 2 }}
      />

      <FormProvider {...methods}>
        <form onSubmit={(event) => void methods.handleSubmit(onSubmit)(event)}>
          <Grid container spacing={4} sx={{ marginBottom: '22px' }}>
            <Grid size={{ xs: 12, md: 6 }}>
              <AgreementAndApprovalsSummary
                advancePayment={{
                  min: MIN_LOAN_AMOUNT,
                  max: loanLimitAvailable.available,
                }}
                details={{
                  repayment: loanData.loanRepaymentPrice,
                  currency: loanLimitAvailable.currency,
                  payoff: loanData.loanRepaymentPrice,
                  period: loanData.period,
                  pricing: loanData.pricing,
                }}
                isSelectedLibierisLoan={isSelectedLibierisLoan}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <Stack direction="column" justifyContent="space-between" sx={{ height: '100%' }}>
                <ShopList merchantResponseData={merchantResponseData} />
                {!isSelectedLibierisLoan ? (
                  <Documents
                    currentProvider={selectedProvider}
                    documents={loanData.documents}
                    loanId={loanData.id}
                  />
                ) : null}
              </Stack>
            </Grid>
          </Grid>
          <Button
            color="secondary"
            data-testid="agreement-and-approvals-submit-button"
            disabled={isEditMode || isApplicationProcessing}
            onClick={() => {
              if (isSelectedLibierisLoan) {
                trackClick({
                  activity: 'LIBERIS_SUMMARY_ACCEPT',
                  previousActivity: 'OFFER_CHOSEN',
                  nextActivity: 'LIBERIS_AGREEMENT_SIGN',
                });
              } else if (selectedProvider === providerType.enum.pragma) {
                trackClick({
                  activity: 'PRAGMA_SUMMARY_ACCEPT',
                  previousActivity: 'OFFER_CHOSEN',
                  nextActivity: 'PRAGMA_AGREEMENT_SIGN',
                });
              }
              handleAcceptTerms();
            }}
            sx={{
              paddingY: 1.5,
              paddingX: 9,
              display: 'flex',
              marginX: 'auto',
            }}
            type="button"
            variant="contained"
          >
            {isSelectedLibierisLoan
              ? t('loanProcess.loanSteps.agreementAndApprovals.liberisButton')
              : t('loanProcess.loanSteps.agreementAndApprovals.button')}
          </Button>
        </form>
      </FormProvider>
    </Box>
  );
}
